import { AnsiUp } from 'ansi_up';
import { useMemo } from 'react';
import { formatAppName } from '../lib/appName';
import { formatTimestamp } from '../lib/history';
import type { DeploymentQueueItem, PlanResult } from '../types';
import { CopyableVersionTag } from './CopyableVersionTag';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

interface PlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: PlanResult;
  enabledEnvironments: string[];
  queuedEnvironments: string[];
  onEnqueueDeployment: (item: DeploymentQueueItem) => void;
  onDequeueDeployment: (environment: string) => void;
}

export const PlanDialog = ({
  open,
  onOpenChange,
  plan,
  enabledEnvironments,
  queuedEnvironments,
  onEnqueueDeployment,
  onDequeueDeployment,
}: PlanDialogProps) => {
  const getStatusLabel = (hasChanges: boolean) => {
    return hasChanges ? 'Changes Detected' : 'No Changes';
  };

  const getStatusColorClass = (hasChanges: boolean) => {
    return hasChanges
      ? 'text-yellow-700 bg-yellow-50 border-yellow-200'
      : 'text-green-700 bg-green-50 border-green-200';
  };

  // Convert ANSI codes to HTML
  const formattedPlanOutput = useMemo(() => {
    if (!plan.planOutput) return null;
    const ansiUp = new AnsiUp();
    return ansiUp.ansi_to_html(plan.planOutput);
  }, [plan.planOutput]);

  const envColorClass =
    plan.environment === 'prod'
      ? 'bg-green-100 text-green-800'
      : plan.environment === 'dev'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-gray-100 text-gray-700';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl max-h-[90vh] overflow-hidden flex flex-col'>
        <DialogHeader>
          <div className='flex items-start justify-between gap-3'>
            <div className='flex-1 min-w-0'>
              <DialogTitle>{formatAppName(plan.appName)}</DialogTitle>
            </div>
            <div className='flex items-center gap-1.5 flex-shrink-0'>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${envColorClass}`}>
                {plan.environment}
              </span>
              <CopyableVersionTag version={plan.version} />
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4 flex-1 overflow-hidden flex flex-col'>
          {/* Plan metadata */}
          <div className='space-y-3 flex-shrink-0'>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Status
              </div>
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium border ${getStatusColorClass(plan.hasChanges)}`}
              >
                {getStatusLabel(plan.hasChanges)}
              </span>
            </div>
            <div>
              <div className='text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5'>
                Scanned at
              </div>
              <div className='text-sm text-gray-900'>{formatTimestamp(plan.createdAt)}</div>
            </div>
          </div>

          {/* Terraform plan output */}
          {formattedPlanOutput ? (
            <div className='flex-1 overflow-hidden flex flex-col min-h-0'>
              <div className='text-sm font-medium text-gray-900 mb-2 flex-shrink-0'>
                Terraform Plan Output:
              </div>
              <div className='bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto flex-1'>
                <pre
                  className='text-xs font-mono whitespace-pre-wrap break-words'
                  dangerouslySetInnerHTML={{ __html: formattedPlanOutput }}
                />
              </div>
            </div>
          ) : (
            <div className='p-4 bg-gray-50 border border-gray-200 rounded-lg text-center text-sm text-gray-500 flex-shrink-0'>
              No plan output available
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {plan.hasChanges && (
            <>
              {(['dev', 'prod'] as const)
                .filter((environment) => enabledEnvironments.includes(environment))
                .map((environment) => {
                  const isQueued = queuedEnvironments.includes(environment);
                  return (
                    <Button
                      key={environment}
                      onClick={() => {
                        if (isQueued) {
                          onDequeueDeployment(environment);
                          return;
                        }

                        onEnqueueDeployment({
                          appName: plan.appName,
                          environment,
                          version: plan.version,
                        });
                      }}
                      className={
                        isQueued
                          ? 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                          : undefined
                      }
                      title={isQueued ? 'Cancel deployment' : undefined}
                    >
                      {isQueued
                        ? `${environment === 'prod' ? 'Prod' : 'Dev'} Queued`
                        : `Deploy ${environment === 'prod' ? 'Prod' : 'Dev'}`}
                    </Button>
                  );
                })}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
